import { convertToIST, getBase64 } from "./appUtils";

/**
 * BUILD EVENT PAYLOAD
 * Transforms form values into API payload structure matching EventRequest schema
 *
 * API Schema (EventRequest):
 * - event_name: string (required)
 * - event_description: string (required)
 * - event_start_date_time: datetime (required)
 * - event_end_date_time: datetime (required)
 * - venue_id: integer (required)
 * - category_id: integer (required)
 * - sub_category_id: integer (required)
 * - event_type_id: integer (required)
 * - event_promo_tags: string | null
 * - event_tags: string | null
 * - event_contact_list: string | null
 * - is_event_published: boolean (default: false)
 * - is_active: boolean (default: true)
 * - is_completed: boolean (default: false)
 * - event_allow_registration: boolean (default: false)
 * - event_allow_promotion: boolean (default: false)
 * - is_drafted_event: boolean (default: false)
 * - is_delete: boolean (default: false)
 * - is_refundable: boolean (default: false)
 * - is_taxable: boolean (default: false)
 * - add_convenience_fee: boolean (default: false)
 * - tickets: array of EventTicketCreate
 * - media: array of MediaRequest
 */
export const buildEventPayload = (values, processedGallery, isDraft = false, isPublished = false) => {
    // Filter out null media items from processed gallery
    const validMedia = (processedGallery || []).filter(m => m !== null);

    // Build tickets array from ticketCategories
    const ticketMode = values.ticketMode || 'per_seat';

    const tickets = values.eventType === '1' || ticketMode === 'per_table' ? [] : (values.ticketCategories || [])
        .filter(t => t && t.seatingCategory)
        .map((ticket) => {
            if (ticket?.id) {
                return {
                    id: ticket.id,
                    ticket_category_id: parseInt(ticket.seatingCategory) || 0,
                    currency_id: parseInt(ticket.currency) || 1,
                    seat_count: parseInt(ticket.numberOfSeats) || 0,
                    price: parseFloat(ticket.price) || 0,
                }
            } else {
                return {
                    ticket_category_id: parseInt(ticket.seatingCategory) || 0,
                    currency_id: parseInt(ticket.currency) || 1,
                    seat_count: parseInt(ticket.numberOfSeats) || 0,
                    price: parseFloat(ticket.price) || 0,
                }
            }
        });

    const tables = values.eventType === '1' || ticketMode !== 'per_table' ? [] : (values.eventTables || [])
        .filter(t => t && t.label)
        .map((table) => {
            const row = {
                label: table.label,
                persons_count: parseInt(table.personsCount) || 1,
                currency_id: parseInt(table.currency) || 1,
                price: parseFloat(table.price) || 0,
                quantity: parseInt(table.quantity) || 1,
            };
            if (table?.id) {
                row.id = table.id;
            }
            return row;
        });

    return {
        // Required fields
        event_name: values['event-name'],
        event_description: values.eventDescription,
        event_start_date_time: convertToIST(values.startDate),
        event_end_date_time: convertToIST(values.endDate),

        // Integer IDs - as required by API (renamed from event_venue, event_category, etc.)
        venue_id: parseInt(values.venue) || 1,
        category_id: parseInt(values.category) || 1,
        sub_category_id: parseInt(values.subcategory) || 1,
        event_type_id: parseInt(values.eventType) || 1,

        // Optional date fields
        event_promo_start_date: values.promoStartDate ? convertToIST(values.promoStartDate) : null,
        event_promo_end_date: values.promoEndDate ? convertToIST(values.promoEndDate) : null,

        // Tags - comma-separated strings
        event_promo_tags: values.promoTag || null,
        event_tags: values.eventTags || null,

        // Contact list
        event_contact_list: (values.contacts || values.loading_contacts || []).toString() || null,

        // Boolean flags
        is_event_published: isPublished,
        is_active: values.is_active !== undefined ? values.is_active : true,
        is_completed: false,
        event_allow_registration: values.event_allow_registration || false,
        event_allow_promotion: values.event_allow_promotion || false,
        is_drafted_event: isDraft,
        is_delete: false,
        is_refundable: values.isRefundable || false,
        is_taxable: values.isTaxable || false,
        add_convenience_fee: values.addConvenienceFee || false,

        ticket_mode: ticketMode,

        // Tickets array
        tickets: tickets,

        // Tables array (per_table mode)
        tables: tables,

        // Media array
        media: validMedia,
    };
};


/**
 * BUILD VENUE PAYLOAD
 * Transforms form values into API payload structure matching VenueRequest schema
 * 
 * API Schema (VenueRequest):
 * - name: string (required)
 * - description: string | null
 * - banner: string | null
 * - address_line1: string (required)
 * - address_line2: string | null
 * - country: integer (required)
 * - state: integer (required)
 * - city: integer (required)
 * - pincode: string | null
 * - landmark: string | null
 * - capacity: integer (required)
 * - tags: string | null
 * - is_drafted: boolean (default: true)
 * - is_active: boolean (default: true)
 * - media: array of MediaRequest
 */
export const formatVenueData = async (values, isDraft = false, payloadType = 'create') => {
    const rawImages = values.venueImages || [];

    // Process images to media format
    const imagePromises = rawImages.map(async (fileObj) => {
        const file = fileObj.originFileObj;
        if (!file) return null;

        const base64 = await getBase64(file);
        const fileName = file.name;
        const lastDotIndex = fileName.lastIndexOf('.');
        // const name = lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
        const extension = lastDotIndex !== -1 ? fileName.substring(lastDotIndex + 1) : '';

        return {
            file_type: "VENUE",
            file_location: "gallery",
            file: base64,
            file_extension: extension
        };
    });

    const media = (await Promise.all(imagePromises)).filter(img => img !== null);

    // Build API payload matching VenueRequest schema
    const payload = {
        name: values.venueName,
        description: values.venueDescription || null,
        // [FIX] Set banner to first image's base64 if available. 
        // The API returns null for banner if we don't explicitly set it, so we fallback to the first uploaded image.
        banner: media.length > 0 ? media[media.length - 1].file : null,
        address_line1: values.addressLine1,
        address_line2: values.addressLine2 || null,
        pincode: values.pincode || null,
        landmark: values.landmark || null,
        capacity: parseInt(values.capacity) || 0,
        tags: values.tags || null,
        is_drafted: isDraft,
        is_active: true,
        media: media
    };

    if (payloadType === 'create') {
        payload.country = parseInt(values.country);
        payload.state = parseInt(values.state);
        payload.city = parseInt(values.city);
    } else if (payloadType === 'update') {
        payload.country_id = parseInt(values.country);
        payload.state_id = parseInt(values.state);
        payload.city_id = parseInt(values.city);
    }

    return payload;
};